/**
 * 콘텐츠 관리 > 특강 탭.
 * 오늘 수업에 배치할 수 있는 단원평가 특강 세트를 read-only로 조회·미리보기한다.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { getQuizSet, type QuizSetDetail } from '@/api/quizSet'
import type { UaCatalogSet } from '@/api/unitAssessmentAdmin'
import { QuizPreviewPanel } from '@/components/admin/diagnostics/QuizPreviewPanel'
import { useUnitAssessmentCatalog } from '@/hooks/coaching/useUnitAssessments'

function flattenQuizIds(detail: QuizSetDetail | undefined): string[] {
  if (!detail) return []

  const quizIds: string[] = []
  const members = [...detail.members].sort((a, b) => a.sequenceOrder - b.sequenceOrder)
  for (const member of members) {
    if (member.kind === 'group' && member.group) {
      const items = [...member.group.items].sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      for (const item of items) quizIds.push(String(item.quiz.quizId))
    } else if (member.quiz) {
      quizIds.push(String(member.quiz.quizId))
    }
  }
  return quizIds
}

export function UnitAssessmentSetsTab() {
  const { t } = useTranslation('admin')
  const catalogQuery = useUnitAssessmentCatalog()
  const [inspectedSet, setInspectedSet] = useState<UaCatalogSet | null>(null)

  if (inspectedSet) {
    return <UnitAssessmentSetInspector set={inspectedSet} onClose={() => setInspectedSet(null)} />
  }

  const catalog = catalogQuery.data

  return (
    <div className="space-y-8">
      <p className="text-sm text-gray-500">{t('unitAssessmentSets.readOnlyNotice')}</p>
      {catalogQuery.isError ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-rose-600 shadow">
          {t('unitAssessmentSets.loadFailed')}
        </div>
      ) : catalogQuery.isLoading ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">…</div>
      ) : !catalog || catalog.sets.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          {t('unitAssessmentSets.empty')}
        </div>
      ) : (
        catalog.grades.map((grade) => (
          <UnitAssessmentSetGroup
            key={grade}
            grade={grade}
            sets={catalog.sets.filter((set) => set.grade === grade)}
            onInspect={setInspectedSet}
          />
        ))
      )}
    </div>
  )
}

function UnitAssessmentSetGroup({
  grade,
  sets,
  onInspect,
}: {
  grade: number
  sets: UaCatalogSet[]
  onInspect: (set: UaCatalogSet) => void
}) {
  const { t } = useTranslation('admin')

  return (
    <section aria-labelledby={`unit-assessment-grade-${grade}`}>
      <h2 id={`unit-assessment-grade-${grade}`} className="mb-3 text-lg font-semibold text-gray-900">
        {t('unitAssessmentSets.gradeGroup', { grade })}
      </h2>
      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">{t('unitAssessmentSets.name')}</th>
              <th className="px-4 py-3">{t('unitAssessmentSets.semester')}</th>
              <th className="px-4 py-3">{t('unitAssessmentSets.unit')}</th>
              <th className="px-4 py-3">{t('unitAssessmentSets.questionCount')}</th>
              <th className="px-4 py-3 text-right">{t('unitAssessmentSets.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sets.map((set) => (
              <tr key={set.quizSetId} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{set.name}</td>
                <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                  {t('unitAssessmentSets.semesterValue', { semester: set.semester })}
                </td>
                <td className="px-4 py-3 text-gray-700">
                  {t('unitAssessmentSets.unitValue', { unit: set.unitNo, name: set.unitName })}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                  {t('unitAssessmentSets.questionCountValue', { count: set.memberCount })}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onInspect(set)}
                    className="text-primary-600 hover:underline"
                  >
                    {t('unitAssessmentSets.preview')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function UnitAssessmentSetInspector({ set, onClose }: { set: UaCatalogSet; onClose: () => void }) {
  const { t } = useTranslation('admin')
  const detailQuery = useQuery({
    queryKey: ['quiz-set-detail', set.quizSetId],
    queryFn: () => getQuizSet(set.quizSetId),
  })
  const quizIds = useMemo(() => flattenQuizIds(detailQuery.data), [detailQuery.data])
  const [current, setCurrent] = useState(0)
  const total = quizIds.length

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {t('unitAssessmentSets.backToList')}
        </button>
        <h2 className="text-base font-semibold text-gray-800">{set.name}</h2>
        <span className="ml-auto text-sm text-gray-500">
          {total > 0 ? `${current + 1} / ${total}` : '0 / 0'}
        </span>
      </div>

      {detailQuery.isLoading ? (
        <div className="p-8 text-center text-gray-500">…</div>
      ) : detailQuery.isError ? (
        <div className="rounded-lg border border-dashed border-rose-200 bg-rose-50 py-12 text-center text-sm text-rose-600">
          {t('unitAssessmentSets.previewLoadFailed')}
        </div>
      ) : total === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-12 text-center text-sm text-gray-400">
          {t('unitAssessmentSets.questionEmpty')}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {quizIds.map((quizId, index) => (
              <button
                key={`${quizId}-${index}`}
                type="button"
                onClick={() => setCurrent(index)}
                aria-label={t('unitAssessmentSets.questionNumber', { number: index + 1 })}
                aria-current={index === current ? 'true' : undefined}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-medium transition-colors ${
                  index === current
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-primary-400'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <QuizPreviewPanel quizId={quizIds[current]} showExplanationVideo />
          </div>
        </>
      )}
    </div>
  )
}
